/**
 * aiSafePlate MVP - React Root Component
 * Tab-based navigation with dark/light mode toggle
 * Responsive mobile-first design
 */

import React, { useState, useEffect, createContext } from 'react';
import { Home, Barcode, MapPin, UtensilsCrossed, BookOpen } from 'lucide-react';
import Dashboard from './components/Dashboard/Dashboard';
import ProductScanner from './components/ProductScanner/ProductScanner';
import DiningOut from './components/DiningOut/DiningOut';
import MealPlanning from './components/MealPlanning/MealPlanning';
import Resources from './components/Resources/Resources';
import Profile from './components/Profile/Profile';
import Reactions from './components/Reactions/Reactions';
import Header from './components/common/Header';
import TabBar from './components/common/TabBar';
import useTheme from './hooks/useTheme';
import useAuth from './hooks/useAuth';
import './styles/theme.css';

// Create context for theme
export const ThemeContext = createContext();

function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showProfile, setShowProfile] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  if (!isAuthenticated) {
    return <LoginFlow />;
  }

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: Home, component: Dashboard },
    { id: 'scanner', name: 'Scanner', icon: Barcode, component: ProductScanner },
    { id: 'dining', name: 'Dining Out', icon: MapPin, component: DiningOut },
    { id: 'meals', name: 'Meal Plan', icon: UtensilsCrossed, component: MealPlanning },
    { id: 'resources', name: 'Resources', icon: BookOpen, component: Resources },
  ];

  const ActiveComponent = tabs.find((t) => t.id === activeTab)?.component || Dashboard;

  // Switching tabs closes any overlay.
  const selectTab = (id) => {
    setShowProfile(false);
    setShowReactions(false);
    setActiveTab(id);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <div className={`app-container theme-${theme}`}>
        {/* Header with theme toggle */}
        <Header
          user={user}
          theme={theme}
          toggleTheme={toggleTheme}
          onLogout={logout}
          onProfile={() => {
            setShowReactions(false);
            setShowProfile(true);
          }}
        />

        {/* Main content */}
        <main className="main-content">
          {showProfile ? (
            <Profile onClose={() => setShowProfile(false)} />
          ) : showReactions ? (
            <Reactions onClose={() => setShowReactions(false)} />
          ) : (
            <ActiveComponent onOpenReactions={() => setShowReactions(true)} />
          )}
          <footer className="app-footer">Dedicated to my wife, Nessa&hellip;</footer>
        </main>

        {/* Bottom navigation */}
        <TabBar tabs={tabs} activeTab={activeTab} setActiveTab={selectTab} />
      </div>
    </ThemeContext.Provider>
  );
}

// Shared, pre-seeded demo identity. Allergies: Peanuts (3) + Milk (1), so the
// seeded demo products scan to a clear mix of RED / YELLOW / GREEN.
const DEMO_CREDENTIALS = {
  email: 'demo@aisafeplate.aibuell.com',
  password: 'demo-allergy-safe',
  firstName: 'Demo',
  allergies: [3, 1],
};

/**
 * Login Flow Component
 * MVP version: simple email/password login + signup
 * TODO: Integrate with Firebase Auth for production
 */
function LoginFlow() {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    allergies: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, signup } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await signup(formData.email, formData.password, formData.firstName, formData.allergies);
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  // Shared demo account (peanut + milk allergy) so visitors can scan right away
  // without signing up. Self-provisions on first use: log in if it exists, else
  // create it. Survives redeploys — no backend seed needed.
  const handleDemo = async () => {
    setLoading(true);
    setError('');
    try {
      try {
        await login(DEMO_CREDENTIALS.email, DEMO_CREDENTIALS.password);
      } catch {
        // Account doesn't exist yet — create it, then we're logged in. If signup
        // races/loses (already exists), fall back to logging in.
        try {
          await signup(
            DEMO_CREDENTIALS.email,
            DEMO_CREDENTIALS.password,
            DEMO_CREDENTIALS.firstName,
            DEMO_CREDENTIALS.allergies
          );
        } catch {
          await login(DEMO_CREDENTIALS.email, DEMO_CREDENTIALS.password);
        }
      }
    } catch (err) {
      setError(err.message || 'Could not start the demo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">SafePlate</h1>
        <p className="auth-subtitle">Food Allergy Safety</p>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="alert alert-error">{error}</div>}

          <input
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            className="input"
          />

          <input
            type="password"
            placeholder="Password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
            className="input"
          />

          {!isLogin && (
            <>
              <input
                type="text"
                placeholder="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="input"
              />

              <div className="allergy-selector">
                <label>Select Your Allergies</label>
                <div className="allergy-grid">
                  {[
                    { id: 1, name: 'Milk' },
                    { id: 2, name: 'Eggs' },
                    { id: 3, name: 'Peanuts' },
                    { id: 4, name: 'Tree Nuts' },
                    { id: 5, name: 'Shellfish' },
                    { id: 6, name: 'Fish' },
                    { id: 7, name: 'Wheat' },
                    { id: 8, name: 'Soy' },
                    { id: 9, name: 'Sesame' },
                    { id: 10, name: 'Mustard' },
                  ].map((allergen) => (
                    <label key={allergen.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.allergies.includes(allergen.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              allergies: [...formData.allergies, allergen.id],
                            });
                          } else {
                            setFormData({
                              ...formData,
                              allergies: formData.allergies.filter((a) => a !== allergen.id),
                            });
                          }
                        }}
                      />
                      <span>{allergen.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          <button type="submit" disabled={loading} className="btn btn-primary btn-full">
            {loading ? 'Loading...' : isLogin ? 'Login' : 'Sign Up'}
          </button>
        </form>

        <button
          onClick={() => {
            setIsLogin(!isLogin);
            setError('');
          }}
          className="btn btn-secondary btn-full"
        >
          {isLogin ? "Don't have an account? Sign Up" : 'Already have an account? Login'}
        </button>

        <div className="auth-divider"><span>or</span></div>

        <button
          type="button"
          onClick={handleDemo}
          disabled={loading}
          className="btn btn-ghost btn-full"
        >
          🍿 Try a demo (no sign-up)
        </button>
        <p className="auth-demo-hint">
          Logs you into a sample account (peanut + milk allergy) so you can scan right away.
        </p>

        <p className="auth-disclaimer">
          ⚠️ This app is educational only. Always consult with your allergist and read product labels.
        </p>

        <p className="auth-dedication">Dedicated to my wife, Nessa&hellip;</p>
      </div>
    </div>
  );
}

export default App;
